import React, { useRef, useEffect, useCallback } from "react";

/**
 * Wraps a button/link so it drifts a couple of pixels toward the cursor while
 * hovered, then eases home. Movement is capped at MAX_SHIFT — it should be
 * felt more than seen.
 *
 * Positions are written straight to the node inside rAF, so hovering never
 * triggers a React render.
 */
const MAX_SHIFT = 3.5;
const PULL = 0.2;
const EASE = 0.14;

export default function MagneticButton({ children, disabled = false }) {
  const hostRef = useRef(null);
  const innerRef = useRef(null);
  const state = useRef({ tx: 0, ty: 0, x: 0, y: 0, raf: null, running: false });

  const step = useCallback(() => {
    const s = state.current;
    const node = innerRef.current;
    if (!node) {
      s.running = false;
      return;
    }

    const dx = s.tx - s.x;
    const dy = s.ty - s.y;
    s.x += dx * EASE;
    s.y += dy * EASE;
    node.style.transform = `translate3d(${s.x.toFixed(2)}px, ${s.y.toFixed(2)}px, 0)`;

    if (Math.abs(dx) < 0.05 && Math.abs(dy) < 0.05) {
      s.x = s.tx;
      s.y = s.ty;
      node.style.transform = `translate3d(${s.x}px, ${s.y}px, 0)`;
      s.running = false;
      s.raf = null;
      return;
    }
    s.raf = requestAnimationFrame(step);
  }, []);

  const wake = useCallback(() => {
    const s = state.current;
    if (s.running) return;
    s.running = true;
    s.raf = requestAnimationFrame(step);
  }, [step]);

  const handleMove = (e) => {
    if (disabled || !hostRef.current) return;
    const { left, top, width, height } = hostRef.current.getBoundingClientRect();
    const dx = e.clientX - (left + width / 2);
    const dy = e.clientY - (top + height / 2);
    const s = state.current;
    s.tx = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, dx * PULL));
    s.ty = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, dy * PULL));
    wake();
  };

  const handleLeave = () => {
    const s = state.current;
    s.tx = 0;
    s.ty = 0;
    wake();
  };

  useEffect(() => {
    const s = state.current;
    return () => {
      if (s.raf) cancelAnimationFrame(s.raf);
    };
  }, []);

  useEffect(() => {
    const reduceMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isFinePointer =
      window.matchMedia && window.matchMedia("(pointer: fine)").matches;
    if ((reduceMotion || !isFinePointer) && innerRef.current) {
      innerRef.current.style.transform = "none";
    }
  }, []);

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    (window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      !window.matchMedia("(pointer: fine)").matches);

  if (disabled || reduceMotion) {
    return <>{children}</>;
  }

  return (
    <span
      ref={hostRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ display: "inline-block" }}
    >
      <span ref={innerRef} style={{ display: "inline-block", willChange: "transform" }}>
        {children}
      </span>
    </span>
  );
}
